import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const RELATIONSHIPS = [
  ["friend", "Friend", "Amigo/a"],
  ["family", "Family", "Familia"],
  ["former_coworker", "Former coworker", "Excompañero/a de trabajo"],
  ["neighbor", "Neighbor", "Vecino/a"],
  ["other", "Other", "Otro"],
];

export default function VouchForm() {
  const { member } = useParams();
  const { lang } = useLang();
  const [m, setM] = useState(undefined);
  const [form, setForm] = useState({ author_name: "", display_name_pref: "first_name", author_email: "", relationship: "friend", years_known: "" });
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get(`/team-members/${member}`).then((r) => setM(r.data)).catch(() => setM(null)); }, [member]);

  const es = lang === "es";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.author_name.trim()) return toast.error(es ? "Escriba su nombre" : "Please enter your name");
    if (!consent || !agree) return toast.error(es ? "Confirme su consentimiento y la política" : "Please confirm consent and the policy");
    setBusy(true);
    try {
      await api.post(`/vouch/${member}`, {
        ...form, language: lang,
        answers: (m.questions || []).map((q) => ({ question_id: q.id, text: answers[q.id] || "" })),
        consent_publish: consent, agree_policy: agree,
      });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (m === undefined) return null;
  if (m === null) return <div className="mx-auto max-w-lg px-6 py-20 text-center text-charcoal-600" data-testid="vouch-invalid">{es ? "No encontramos a esta persona." : "We couldn't find this team member."}</div>;
  if (!m.accepting_vouches) return <div className="mx-auto max-w-lg px-6 py-20 text-center text-charcoal-600" data-testid="vouch-closed">{es ? "Esta persona no está recibiendo referencias por ahora." : "This team member is not accepting references right now."}</div>;

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-6 py-20 text-center" data-testid="vouch-thankyou">
        <CheckCircle2 size={48} className="text-sage-600 mx-auto mb-4" />
        <h1 className="text-2xl font-normal text-charcoal-800 mb-2">{es ? "¡Gracias!" : "Thank you!"}</h1>
        <p className="text-charcoal-600 mb-6">{es ? "Revisaremos su referencia antes de publicarla." : "We'll review your reference before it's published."}</p>
        <Link to="/references" className="text-charcoal-500 underline text-sm">{es ? "Volver a referencias" : "Back to references"}</Link>
      </div>
    );
  }

  const inputCls = "mt-1 w-full border border-sand-200 rounded-xl px-3 py-2 text-sm";
  return (
    <div className="mx-auto max-w-lg px-6 py-12" data-testid="vouch-form">
      <h1 className="text-2xl font-normal text-charcoal-800 mb-1">{es ? `Una referencia para ${m.name}` : `A reference for ${m.name}`}</h1>
      <p className="text-sm text-charcoal-500 mb-6">{es ? m.role_es : m.role_en} · {es ? "No es una reseña de cliente y no cuenta en las estrellas." : "Not a client review — not counted in our stars."}</p>

      <label className="block mb-4"><span className="text-sm text-charcoal-600">{es ? "Su nombre" : "Your name"}</span>
        <input data-testid="vouch-name" className={inputCls} value={form.author_name} onChange={(e) => set("author_name", e.target.value)} /></label>

      <label className="block mb-4"><span className="text-sm text-charcoal-600">{es ? "Cómo mostrar su nombre" : "How to show your name"}</span>
        <select data-testid="vouch-displaypref" className={inputCls} value={form.display_name_pref} onChange={(e) => set("display_name_pref", e.target.value)}>
          <option value="first_name">{es ? "Solo nombre (p. ej. María)" : "First name (e.g. Maria)"}</option>
          <option value="initials">{es ? "Iniciales (p. ej. M.G.)" : "Initials (e.g. M.G.)"}</option>
        </select></label>

      <label className="block mb-4"><span className="text-sm text-charcoal-600">{es ? "Su correo (privado, no se publica)" : "Your email (private, never published)"}</span>
        <input data-testid="vouch-email" type="email" className={inputCls} value={form.author_email} onChange={(e) => set("author_email", e.target.value)} /></label>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="block"><span className="text-sm text-charcoal-600">{es ? "Relación" : "Relationship"}</span>
          <select data-testid="vouch-relationship" className={inputCls} value={form.relationship} onChange={(e) => set("relationship", e.target.value)}>
            {RELATIONSHIPS.map(([k, en, esl]) => <option key={k} value={k}>{es ? esl : en}</option>)}
          </select></label>
        <label className="block"><span className="text-sm text-charcoal-600">{es ? "Años que lo/la conoce" : "Years known"}</span>
          <input data-testid="vouch-years" className={inputCls} value={form.years_known} onChange={(e) => set("years_known", e.target.value)} placeholder={es ? "p. ej. 5" : "e.g. 5"} /></label>
      </div>

      {(m.questions || []).map((q) => (
        <label key={q.id} className="block mb-4"><span className="text-sm text-charcoal-600">{es ? q.text_es : q.text_en}</span>
          <textarea data-testid={`vouch-answer-${q.id}`} rows={3} maxLength={800} className={inputCls} value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} /></label>
      ))}

      <label className="flex items-start gap-2 mb-3 text-sm text-charcoal-600">
        <input type="checkbox" data-testid="vouch-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
        <span>{es ? "Doy mi permiso para que Hola Movers publique esta referencia." : "I give Hola Movers permission to publish this reference."}</span>
      </label>
      <label className="flex items-start gap-2 mb-6 text-sm text-charcoal-600">
        <input type="checkbox" data-testid="vouch-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" />
        <span>{es ? "Esto es honesto y conozco a esta persona. Acepto la " : "This is honest and I know this person. I agree to the "}<Link to="/review-policy" className="text-terracotta underline">{es ? "Política de Reseñas" : "Review Policy"}</Link>.</span>
      </label>

      <button onClick={submit} disabled={busy} data-testid="vouch-submit" className="w-full rounded-full bg-terracotta text-white py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 size={16} className="animate-spin" /> : null}{es ? "Enviar referencia" : "Submit reference"}
      </button>
    </div>
  );
}
